import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const HealthcareProvidersPage: React.FC = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const [heroImage, setHeroImage] = useState<string>('');
  const [problemImage, setProblemImage] = useState<string>('');
  const [crisisImage, setCrisisImage] = useState<string>('');
  const [solutionImage, setSolutionImage] = useState<string>('');

  useEffect(() => {
    // Fetch images from backend
    fetch(`${API_URL}/api/images`)
      .then(res => res.json())
      .then((data: any) => {
        const images = data.images || [];
        
        // Helper function to normalize filename for searching
        const normalize = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
        
        // Look for healthcare hero image - healthcareLaborShortageWindow.png
        const hero = images.find((img: any) => {
          const filename = (img.filename || '').toLowerCase();
          const normalized = normalize(img.filename || '');
          return filename === 'healthcarelaborshortagewindow.png' ||
                 normalized.includes('healthcarelaborshortagewindow');
        });
        if (hero) {
          setHeroImage(`${API_URL}/api/images/${hero.id}`);
        }

        // Look for problem section image - healthcareLaborShortageBed.png
        const problem = images.find((img: any) => {
          const filename = (img.filename || '').toLowerCase();
          const normalized = normalize(img.filename || '');
          return filename === 'healthcarelaborshortagebed.png' ||
                 normalized.includes('healthcarelaborshortagebed');
        });
        if (problem) {
          setProblemImage(`${API_URL}/api/images/${problem.id}`);
        }

        // Look for crisis section image - healthcareLaborShortageMask.png
        const crisis = images.find((img: any) => {
          const filename = (img.filename || '').toLowerCase();
          const normalized = normalize(img.filename || '');
          return filename === 'healthcarelaborshortagemask.png' ||
                 normalized.includes('healthcarelaborshortagemask');
        });
        if (crisis) {
          setCrisisImage(`${API_URL}/api/images/${crisis.id}`);
        }

        // Look for solution section image - healthcareLaborShortageMedicalAlertSystem.png
        const solution = images.find((img: any) => {
          const filename = (img.filename || '').toLowerCase();
          const normalized = normalize(img.filename || '');
          return filename === 'healthcarelaborshortagemedicalalertsystem.png' ||
                 normalized.includes('healthcarelaborshortagemedicalalertsystem');
        });
        if (solution) {
          setSolutionImage(`${API_URL}/api/images/${solution.id}`);
        }
      })
      .catch(err => console.error('Error fetching images:', err));
  }, [API_URL]);

  return (
    <div className="healthcare-providers-page">
      {/* Hero Section with Image and Quote */}
      <section className="healthcare-hero-section">
        <div className="healthcare-hero-container">
          <div className="healthcare-hero-content">
            <h1 className="healthcare-hero-title">
              From Crisis to Control:<br />
              Medical Alert Systems<br />
              Close the Labor Shortage Gap
            </h1>
            <div className="healthcare-hero-quote">
              <span className="quote-mark">"</span>
              <p className="quote-text">
                I would almost call it <strong>desperation</strong>, said Pete Van Runkle, Executive Director of the Ohio Health Care Association, which represents <strong>more than 1,200 nursing home, assisted living, and home care providers</strong>. "[There are] day-to-day <strong>staffing challenges</strong> that virtually all of our members are experiencing.<sup>1</sup>
              </p>
              <span className="quote-mark-end">"</span>
            </div>
          </div>
          <div className="healthcare-hero-image">
            {heroImage ? (
              <img src={heroImage} alt="Healthcare professional" className="healthcare-hero-img" />
            ) : (
              <div className="healthcare-hero-placeholder">
                <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                  <circle cx="200" cy="200" r="180" fill="#f0f0f0"/>
                  <circle cx="200" cy="160" r="60" fill="#d0d0d0"/>
                  <ellipse cx="200" cy="300" rx="100" ry="120" fill="#d0d0d0"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="healthcare-problem-section">
        <div className="healthcare-problem-container">
          <div className="healthcare-problem-image">
            {problemImage ? (
              <img src={problemImage} alt="Healthcare professional" className="healthcare-problem-img" />
            ) : (
              <div className="healthcare-problem-placeholder">
                <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                  <circle cx="200" cy="200" r="180" fill="#f0f0f0"/>
                  <circle cx="200" cy="160" r="60" fill="#d0d0d0"/>
                  <ellipse cx="200" cy="300" rx="100" ry="120" fill="#d0d0d0"/>
                </svg>
              </div>
            )}
          </div>
          <div className="healthcare-problem-content">
            <h2 className="healthcare-problem-title">
              The Problem: Extreme Staffing Shortages = No Care for Patients
            </h2>
            <p className="healthcare-problem-text">
              Even before covid-19 was on anyone's radar, the warning bells had sounded that by 2025, the US would experience a healthcare labor shortage resulting from a crush of Baby Boomers needing long-term care.
            </p>
            <p className="healthcare-problem-text">
              That staffing crisis arrived 3 years earlier than predicted, due to the hyper-infectiousness of Omicron and the resulting astronomical rise in cases and hospitalizations. Health systems, many of which were already overburdened following the Delta wave's strain on resources, have had little opportunity for staff and supply shortages to recover.
            </p>
            <button 
              className="healthcare-view-btn"
              onClick={() => navigate('/medical-alerts')}
            >
              VIEW SYSTEMS
            </button>
          </div>
        </div>
      </section>

      {/* Crisis Unfolds Section */}
      <section className="healthcare-crisis-section">
        <div className="healthcare-crisis-container">
          <div className="healthcare-crisis-content">
            <h2 className="healthcare-crisis-title">
              The Care Crisis Unfolds:
            </h2>
            <div className="healthcare-crisis-points">
              <div className="crisis-point">
                <div className="crisis-checkmark">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#4caf50"/>
                    <path d="M7 12 L10 15 L17 8" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <p className="crisis-point-text">
                  Despite Omicron's milder effects, it is shutting down the economy and creating some of the biggest challenges of the pandemic.
                </p>
              </div>
              <div className="crisis-point">
                <div className="crisis-checkmark">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#4caf50"/>
                    <path d="M7 12 L10 15 L17 8" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <p className="crisis-point-text">
                  In NYC, nurses are stretched so thin that some health care facilities either have a fraction of the coverage they should have or no patient monitoring at all.<sup>2</sup>
                </p>
              </div>
              <div className="crisis-point">
                <div className="crisis-checkmark">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#4caf50"/>
                    <path d="M7 12 L10 15 L17 8" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <p className="crisis-point-text">
                  Right now, hospitals and care facilities in California, Arizona, and Rhode Island are allowing covid-infected workers to stay on the job, treating patients, as a result of severe labor shortages and overwhelming demand for care.<sup>3</sup>
                </p>
              </div>
              <div className="crisis-point">
                <div className="crisis-checkmark">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#4caf50"/>
                    <path d="M7 12 L10 15 L17 8" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <p className="crisis-point-text">
                  Nursing homes have suffered the worst labor shortage among all health care facilities, with 221,000 jobs lost since the start of the pandemic, according to a report released by the American Health Care Association (AHCA) and the National Center for Assisted Living (NCAL).<sup>4</sup>
                </p>
              </div>
            </div>
            <button 
              className="healthcare-view-btn"
              onClick={() => navigate('/medical-alerts')}
            >
              VIEW SYSTEMS
            </button>
          </div>
          <div className="healthcare-crisis-image">
            {crisisImage ? (
              <img src={crisisImage} alt="Healthcare worker" className="healthcare-crisis-img" />
            ) : (
              <div className="healthcare-crisis-placeholder">
                <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                  <circle cx="200" cy="200" r="180" fill="#f0f0f0"/>
                  <circle cx="200" cy="160" r="60" fill="#d0d0d0"/>
                  <ellipse cx="200" cy="300" rx="100" ry="120" fill="#d0d0d0"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="healthcare-solution-section">
        <div className="healthcare-solution-container">
          <div className="healthcare-solution-image">
            {solutionImage ? (
              <img src={solutionImage} alt="Medical alert system" className="healthcare-solution-img" />
            ) : (
              <div className="healthcare-solution-placeholder">
                <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
                  <rect x="100" y="100" width="200" height="200" fill="#f0f0f0" rx="8"/>
                  <circle cx="150" cy="150" r="30" fill="#d0d0d0"/>
                  <circle cx="250" cy="150" r="30" fill="#d0d0d0"/>
                  <rect x="150" y="220" width="100" height="60" fill="#d0d0d0" rx="4"/>
                </svg>
              </div>
            )}
          </div>
          <div className="healthcare-solution-content">
            <h2 className="healthcare-solution-title">
              A Long-Term Solution for<br />
              a Long-Term Problem:
            </h2>
            <p className="healthcare-solution-text">
              Help is on stand-by 24/7/365 with the press of a button. Built-in Fall Detection technology is also available for populations who need this life-saving feature.
            </p>
            <div className="healthcare-solution-points">
              <div className="solution-point">
                <div className="solution-checkmark">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#4caf50"/>
                    <path d="M7 12 L10 15 L17 8" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <p className="solution-point-text">Works 24/7/365</p>
              </div>
              <div className="solution-point">
                <div className="solution-checkmark">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#4caf50"/>
                    <path d="M7 12 L10 15 L17 8" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <p className="solution-point-text">Won't Quit or Call Out</p>
              </div>
              <div className="solution-point">
                <div className="solution-checkmark">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#4caf50"/>
                    <path d="M7 12 L10 15 L17 8" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <p className="solution-point-text">Never Gets Sick</p>
              </div>
              <div className="solution-point">
                <div className="solution-checkmark">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#4caf50"/>
                    <path d="M7 12 L10 15 L17 8" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <p className="solution-point-text">Never Gets Stressed</p>
              </div>
              <div className="solution-point">
                <div className="solution-checkmark">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#4caf50"/>
                    <path d="M7 12 L10 15 L17 8" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <p className="solution-point-text">Always on Duty</p>
              </div>
              <div className="solution-point">
                <div className="solution-checkmark">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#4caf50"/>
                    <path d="M7 12 L10 15 L17 8" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <p className="solution-point-text">Affordable Rates (starting at just $19.99/month)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="healthcare-final-cta-section">
        <div className="healthcare-final-cta-container">
          <h2 className="healthcare-final-cta-title">
            Don't Let Your Patients Face an Emergency Alone.
          </h2>
          <button 
            className="healthcare-view-btn healthcare-final-cta-btn"
            onClick={() => navigate('/medical-alerts')}
          >
            VIEW SYSTEMS
          </button>
        </div>
      </section>

      {/* Footnotes Section */}
      <section className="healthcare-footnotes-section">
        <div className="healthcare-footnotes-container">
          <ol className="healthcare-footnotes-list">
            <li className="healthcare-footnote">
              <a href="https://www.wcpo.com/news/local-news/i-team/desperate-nursing-home-operators-struggle-with-covid-staff-shortages" target="_blank" rel="noopener noreferrer">
                Desperate nursing homes struggle with more staff shortages (wcpo.com)
              </a>
            </li>
            <li className="healthcare-footnote">
              <a href="https://gothamist.com/news/nycs-hospital-staffing-crisis-is-fueled-by-omicron-made-worse-by-employee-departures" target="_blank" rel="noopener noreferrer">
                NYC's Hospital Staffing Crisis Is Fueled By Omicron - Made Worse By Employee Departures - Gothamist
              </a>
            </li>
            <li className="healthcare-footnote">
              <a href="https://apnews.com/article/coronavirus-pandemic-business-health-arizona-california-0113146dc7b8fd8a5b6a5624d8e0e30c" target="_blank" rel="noopener noreferrer">
                Health officials let COVID-infected staff stay on the job | AP News
              </a>
            </li>
            <li className="healthcare-footnote">
              <a href="https://www.ahcancal.org/News-and-Communications/Press-Releases/Pages/REPORT-Nursing-Homes-Down-221,000-Jobs-Since-Start-Of-Pandemic.aspx" target="_blank" rel="noopener noreferrer">
                REPORT: Nursing Homes Down 221,000 Jobs Since Start of Pandemic (ahcancal.org)
              </a>
            </li>
          </ol>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HealthcareProvidersPage;

